import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import swal from "sweetalert";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

/* =========================================================
   HELPERS
========================================================= */

const numberFormat = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return new Intl.NumberFormat("en-BD", {
    maximumFractionDigits: 2,
  }).format(number);
};

const money = (value) => {
  return `৳${numberFormat(value)}`;
};

const safeText = (value, fallback = "—") => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  return value;
};

const getError = (value) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value[0] || "";
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
};

/* =========================================================
   PACKAGE HELPERS
========================================================= */

const getPackageName = (project, index) => {
  if (project?.packege_name) {
    return project.packege_name;
  }

  return index === 0
    ? "Agro Growth Package"
    : "Agro Premium Package";
};

const getPackageBadge = (index) => {
  return index === 0 ? "CATEGORY A" : "CATEGORY B";
};

const getPackageSubtitle = (index) => {
  return index === 0
    ? "Flexible Agro Growth Plan"
    : "Premium Agro Return Plan";
};

const getPackageBenefits = (project, index) => {
  const target = Number(project?.price || 100000);
  const booking = Number(
    project?.booking_amount ||
      (index === 0 ? 5000 : 10000)
  );

  if (index === 0) {
    return [
      {
        label: "Minimum Booking",
        value: money(booking),
      },
      {
        label: "Investment Target",
        value: money(target),
      },
      {
        label: "Monthly Return",
        value: "3% – 4%",
      },
      {
        label: "Sponsor Commission",
        value: "5%",
      },
    ];
  }

  return [
    {
      label: "Minimum Booking",
      value: money(booking),
    },
    {
      label: "Investment Target",
      value: money(target),
    },
    {
      label: "Monthly Return",
      value: "10%",
    },
    {
      label: "Maximum Return",
      value: "Up to 2X",
    },
  ];
};

/* =========================================================
   ICONS
========================================================= */

const LeafIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="25"
    height="25"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20.8 3.2C15.2 3.4 9.1 5 6.2 9.1c-2 2.9-1.5 6.4.6 8.2 2.2 1.8 5.7 1.7 8.1-.7 3.8-3.8 4.8-9 5.9-13.4Z" />
    <path d="M4 21c2.6-5.2 6.1-8.9 11.1-11.4" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="23"
    height="23"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3 5 6v5c0 4.8 2.9 8.2 7 10 4.1-1.8 7-5.2 7-10V6l-7-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m5 12 4 4L19 6" />
  </svg>
);

/* =========================================================
   COMPONENT
========================================================= */

const Checkout = () => {
  const authToken = localStorage.getItem("auth_token");

  const [projects, setProjects] = useState([]);

  const [selectedProjectId, setSelectedProjectId] =
    useState("");

  const [form, setForm] = useState({
    amount: "",
    password: "",
    error_list: {},
  });

  const [termsAccepted, setTermsAccepted] =
    useState(false);

  const [showPassword, setShowPassword] =
    useState(false);

  const [fetchLoading, setFetchLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState(false);

  const [pageError, setPageError] =
    useState("");

  /* =======================================================
     LOAD AGRO PACKAGES
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    const loadProjects = async () => {
      if (!authToken) {
        if (mounted) {
          setFetchLoading(false);

          setPageError(
            "Your login session was not found. Please login again."
          );
        }

        return;
      }

      try {
        setFetchLoading(true);
        setPageError("");

        const response = await axios.post(
          "/agro-packege",
          {
            key_s: API_KEY,
          }
        );

        if (!mounted) return;

        if (response?.data?.status === "success") {
          const rows = Array.isArray(
            response?.data?.data
          )
            ? response.data.data
            : [];

          setProjects(rows);
        } else {
          setProjects([]);

          setPageError(
            response?.data?.message ||
              "Unable to load agro investment packages."
          );
        }
      } catch (error) {
        console.error(
          "Agro package loading error:",
          error
        );

        if (!mounted) return;

        setProjects([]);

        setPageError(
          error?.response?.data?.message ||
            "Unable to load agro investment packages. Please try again."
        );
      } finally {
        if (mounted) {
          setFetchLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      mounted = false;
    };
  }, [authToken]);

  /* =======================================================
     SELECTED PACKAGE
  ======================================================= */

  const selectedProject = useMemo(() => {
    return (
      projects.find(
        (project) =>
          String(project.id) ===
          String(selectedProjectId)
      ) || null
    );
  }, [projects, selectedProjectId]);

  const selectedProjectIndex = useMemo(() => {
    if (!selectedProject) {
      return -1;
    }

    return projects.findIndex(
      (project) =>
        String(project.id) ===
        String(selectedProject.id)
    );
  }, [projects, selectedProject]);

  /* =======================================================
     CALCULATIONS
  ======================================================= */

  const investmentTarget = Number(
    selectedProject?.price || 0
  );

  const bookingAmount = Number(
    selectedProject?.booking_amount || 0
  );

  const enteredAmount = Number(
    form.amount || 0
  );

  const totalPayable = enteredAmount;

  const minimumBooking =
    bookingAmount > 0
      ? bookingAmount
      : 1;

  const maximumBooking =
    investmentTarget > 0
      ? investmentTarget
      : 999999999;

  /* =======================================================
     SELECT PACKAGE
  ======================================================= */

  const selectProject = (project) => {
    if (!project) return;

    setSelectedProjectId(
      String(project.id)
    );

    const minimum = Number(
      project.booking_amount || 0
    );

    setForm((previous) => ({
      ...previous,

      amount:
        minimum > 0
          ? minimum
          : "",

      error_list: {
        ...previous.error_list,

        selectedpackege: "",
        amount: "",
      },
    }));
  };

  /* =======================================================
     INPUT
  ======================================================= */

  const handleInput = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (name === "amount") {
      value = value.replace(
        /[^0-9.]/g,
        ""
      );
    }

    setForm((previous) => ({
      ...previous,

      [name]: value,

      error_list: {
        ...previous.error_list,
        [name]: "",
      },
    }));
  };

  /* =======================================================
     QUICK AMOUNT
  ======================================================= */

  const setQuickAmount = (amount) => {
    if (!selectedProject) return;

    const safeAmount = Math.min(
      maximumBooking,
      Math.max(
        minimumBooking,
        Number(amount || 0)
      )
    );

    setForm((previous) => ({
      ...previous,

      amount: safeAmount,

      error_list: {
        ...previous.error_list,
        amount: "",
      },
    }));
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {
    const errors = {};

    if (!selectedProject) {
      errors.selectedpackege =
        "Please select an agro investment package.";
    }

    const amount = Number(
      form.amount
    );

    if (!amount || amount <= 0) {
      errors.amount =
        "Please enter your booking amount.";
    } else if (
      selectedProject &&
      amount < minimumBooking
    ) {
      errors.amount =
        `Minimum booking amount is ${money(
          minimumBooking
        )}.`;
    } else if (
      selectedProject &&
      investmentTarget > 0 &&
      amount > investmentTarget
    ) {
      errors.amount =
        `Booking amount cannot exceed the investment target of ${money(
          investmentTarget
        )}.`;
    }

    if (
      !form.password ||
      !form.password.trim()
    ) {
      errors.password =
        "Account password is required.";
    }

    if (!termsAccepted) {
      errors.terms =
        "Please accept the terms and conditions.";
    }

    setForm((previous) => ({
      ...previous,
      error_list: errors,
    }));

    return (
      Object.keys(errors).length === 0
    );
  };

  /* =======================================================
     SUBMIT AGRO INVESTMENT
  ======================================================= */

  const submitAgroInvestment = async (
    event
  ) => {
    event.preventDefault();

    if (submitting) return;

    if (!authToken) {
      swal(
        "Session Expired",
        "Please login again to continue.",
        "warning"
      );

      return;
    }

    if (!validateForm()) {
      return;
    }

    /*
     * Keep existing backend fields unchanged.
     * numberc remains 1 for compatibility
     * with the current Laravel backend.
     */

    const data = {
      selectedpackege:
        selectedProject.id,

      numberc: 1,

      amount: totalPayable,

      password: form.password,

      key_s: API_KEY,
    };

    try {
      setSubmitting(true);

      const response =
        await axios.post(
          "/sub-agro-invest",
          data
        );

      const result =
        response?.data || {};

      if (
        result.status === "success"
      ) {
        await swal(
          "Booking has been completed successfully",
          result.message ||
            "Your agro investment booking has been completed successfully.",
          "success"
        );

        setSelectedProjectId("");

        setForm({
          amount: "",
          password: "",
          error_list: {},
        });

        setTermsAccepted(false);
        setShowPassword(false);
      } else if (
        result.status === "fail"
      ) {
        swal(
          "Investment Failed",
          result.message ||
            "Your agro investment could not be completed.",
          "warning"
        );
      } else if (
        result.validation_error
      ) {
        setForm(
          (previous) => ({
            ...previous,

            error_list:
              result.validation_error ||
              {},
          })
        );
      } else {
        swal(
          "Unable to Continue",
          result.message ||
            "Something went wrong.",
          "warning"
        );
      }
    } catch (error) {
      console.error(
        "Agro investment error:",
        error
      );

      if (
        error?.response?.status ===
        422
      ) {
        const validationErrors =
          error?.response?.data
            ?.errors ||
          error?.response?.data
            ?.validation_error ||
          {};

        setForm(
          (previous) => ({
            ...previous,

            error_list:
              validationErrors,
          })
        );

        return;
      }

      swal(
        "Something Went Wrong",
        error?.response?.data
          ?.message ||
          "We could not process your agro investment. Please try again.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =======================================================
     ERRORS
  ======================================================= */

  const selectedError = getError(
    form.error_list
      ?.selectedpackege
  );

  const amountError = getError(
    form.error_list?.amount
  );

  const passwordError = getError(
    form.error_list?.password
  );

  const termsError = getError(
    form.error_list?.terms
  );

  /* =======================================================
     SUBMIT STATUS
  ======================================================= */

  const canSubmit =
    Boolean(selectedProject) &&
    Number(form.amount) >=
      minimumBooking &&
    (
      investmentTarget <= 0 ||
      Number(form.amount) <=
        investmentTarget
    ) &&
    Boolean(
      form.password.trim()
    ) &&
    termsAccepted &&
    totalPayable > 0 &&
    !submitting;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Fragment>

      <style>{`

        * {
          box-sizing: border-box;
        }

        /* ================================================
           ROOT
        ================================================ */

        .agro-page {
          --primary: #16803c;
          --primary-dark: #0b5f2a;
          --primary-deep: #084c23;

          --primary-soft: #edf8f1;
          --primary-soft2: #f5fbf7;

          --accent: #84b82c;
          --accent-soft: #f5faeb;

          --warning: #d97706;
          --warning-soft: #fff8eb;

          --danger: #dc2626;

          --text: #17231b;
          --text2: #34473a;
          --muted: #6a776d;

          --border: #e0e9e3;
          --border-dark: #d2dfd6;

          --white: #ffffff;

          min-height: 100vh;
          background:
            radial-gradient(
              circle at 0% 0%,
              rgba(22,128,60,.09),
              transparent 27%
            ),
            radial-gradient(
              circle at 100% 0%,
              rgba(132,184,44,.08),
              transparent 24%
            ),
            #f6f9f7;

          color: var(--text);

          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .agro-shell {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* ================================================
           HERO
        ================================================ */

        .agro-hero {
          position: relative;

          overflow: hidden;

          display: flex;

          align-items: center;
          justify-content: space-between;

          gap: 30px;

          min-height: 225px;

          margin-bottom: 22px;

          padding: 36px;

          border-radius: 26px;

          background:
            linear-gradient(
              135deg,
              #082f1b 0%,
              #0b5c2b 52%,
              #16803c 100%
            );

          box-shadow:
            0 20px 55px
            rgba(8,76,35,.15);
        }

        .agro-hero::before {
          content: "";

          position: absolute;

          width: 360px;
          height: 360px;

          right: -110px;
          top: -215px;

          border-radius: 50%;

          background:
            rgba(255,255,255,.06);
        }

        .agro-hero::after {
          content: "";

          position: absolute;

          width: 260px;
          height: 260px;

          right: 260px;
          bottom: -205px;

          border-radius: 50%;

          background:
            rgba(151,208,60,.14);
        }

        .hero-content {
          position: relative;

          z-index: 2;

          max-width: 760px;
        }

        .hero-label {
          display: inline-flex;

          align-items: center;

          gap: 8px;

          padding: 7px 12px;

          margin-bottom: 14px;

          border:
            1px solid
            rgba(255,255,255,.12);

          border-radius: 50px;

          background:
            rgba(255,255,255,.08);

          color: #bff08c;

          font-size: 12px;
          font-weight: 850;

          letter-spacing: .7px;

          text-transform: uppercase;
        }

        .hero-label-dot {
          width: 8px;
          height: 8px;

          border-radius: 50%;

          background: #b7ec73;

          box-shadow:
            0 0 0 5px
            rgba(183,236,115,.12);
        }

        .agro-hero h1 {
          margin: 0 0 11px;

          color: #fff;

          font-size: 36px;
          font-weight: 900;

          line-height: 1.18;

          letter-spacing: -.5px;
        }

        .agro-hero p {
          max-width: 700px;

          margin: 0;

          color:
            rgba(255,255,255,.74);

          font-size: 15px;

          line-height: 1.75;
        }

        .hero-security {
          position: relative;

          z-index: 2;

          flex: 0 0 245px;

          padding: 20px;

          border:
            1px solid
            rgba(255,255,255,.13);

          border-radius: 19px;

          background:
            rgba(255,255,255,.08);

          backdrop-filter:
            blur(10px);
        }

        .hero-security-icon {
          width: 48px;
          height: 48px;

          display: flex;

          align-items: center;
          justify-content: center;

          margin-bottom: 13px;

          border-radius: 14px;

          background:
            rgba(183,236,115,.14);

          color: #c6f48e;
        }

        .hero-security small {
          display: block;

          margin-bottom: 4px;

          color:
            rgba(255,255,255,.58);

          font-size: 12px;
        }

        .hero-security strong {
          display: block;

          color: #fff;

          font-size: 16px;
          font-weight: 850;

          line-height: 1.4;
        }

        /* ================================================
           MAIN GRID
        ================================================ */


        .agro-panel {
          overflow: hidden;

          border:
            1px solid
            var(--border);

          border-radius: 23px;

          background: #fff;

          box-shadow:
            0 8px 35px
            rgba(25,65,38,.055);
        }

        .agro-panel-body {
          padding: 27px;
        }

        /* ================================================
           SECTION HEADER
        ================================================ */

        .section-head {
          display: flex;

          align-items: flex-start;

          gap: 14px;

          margin-bottom: 22px;
        }

        .section-step {
          flex: 0 0 45px;

          width: 45px;
          height: 45px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 14px;

          background:
            var(--primary-soft);

          color:
            var(--primary);

          font-size: 14px;
          font-weight: 900;
        }

        .section-head h2 {
          margin: 0 0 4px;

          color: var(--text);

          font-size: 22px;
          font-weight: 900;

          line-height: 1.3;
        }

        .section-head p {
          margin: 0;

          color: var(--muted);

          font-size: 13px;

          line-height: 1.6;
        }

        /* ================================================
           PACKAGE GRID
        ================================================ */

        .package-grid {
          display: grid;

          grid-template-columns:
            repeat(2,minmax(0,1fr));

          gap: 17px;
        }

        .package-card {
          position: relative;

          overflow: hidden;

          display: flex;

          flex-direction: column;

          min-height: 390px;

          padding: 21px;

          border:
            1.5px solid
            var(--border);

          border-radius: 20px;

          background: #fff;

          cursor: pointer;

          transition:
            transform .22s ease,
            border-color .22s ease,
            box-shadow .22s ease;
        }

        .package-card:hover {
          transform:
            translateY(-3px);

          border-color:
            #a9cfb5;

          box-shadow:
            0 15px 35px
            rgba(17,100,49,.09);
        }

        .package-card.selected {
          border-color:
            var(--primary);

          background:
            linear-gradient(
              180deg,
              #f1faf4 0%,
              #ffffff 72%
            );

          box-shadow:
            0 0 0 3px
            rgba(22,128,60,.07),
            0 16px 35px
            rgba(22,128,60,.08);
        }

        .package-card.featured::before {
          content: "";

          position: absolute;

          width: 180px;
          height: 180px;

          top: -110px;
          right: -90px;

          border-radius: 50%;

          background:
            rgba(132,184,44,.08);
        }

        .package-radio {
          position: absolute;

          opacity: 0;

          pointer-events: none;
        }

        .package-check {
          position: absolute;

          top: 18px;
          right: 18px;

          width: 30px;
          height: 30px;

          display: flex;

          align-items: center;
          justify-content: center;

          border:
            2px solid
            #d4ddd7;

          border-radius: 50%;

          background: #fff;

          color: transparent;

          transition: .2s ease;
        }

        .package-card.selected
        .package-check {
          border-color:
            var(--primary);

          background:
            var(--primary);

          color: #fff;
        }

        .package-top {
          display: flex;

          align-items: center;

          gap: 12px;

          padding-right: 42px;

          margin-bottom: 16px;
        }

        .package-icon {
          flex: 0 0 50px;

          width: 50px;
          height: 50px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 15px;

          background:
            var(--primary-soft);

          color:
            var(--primary);
        }

        .package-category {
          margin-bottom: 3px;

          color:
            var(--primary);

          font-size: 11px;
          font-weight: 900;

          letter-spacing: .65px;

          text-transform: uppercase;
        }

        .package-name {
          color:
            var(--text);

          font-size: 17px;
          font-weight: 900;

          line-height: 1.35;
        }

        .package-subtitle {
          margin-bottom: 16px;

          color:
            var(--muted);

          font-size: 13px;

          line-height: 1.55;
        }

        /* ================================================
           TARGET
        ================================================ */

        .target-box {
          padding: 15px 16px;

          margin-bottom: 15px;

          border:
            1px solid
            #dcebe1;

          border-radius: 15px;

          background:
            linear-gradient(
              135deg,
              #f2faf5,
              #f8fcf9
            );
        }

        .target-box span {
          display: block;

          margin-bottom: 4px;

          color:
            var(--muted);

          font-size: 11px;
          font-weight: 650;
        }

        .target-box strong {
          display: block;

          color:
            var(--primary);

          font-size: 24px;
          font-weight: 950;

          line-height: 1.2;
        }

        /* ================================================
           BENEFITS
        ================================================ */

        .benefit-list {
          display: grid;

          gap: 9px;

          margin-top: auto;
        }

        .benefit-row {
          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 12px;

          padding: 10px 11px;

          border:
            1px solid
            #edf1ee;

          border-radius: 11px;

          background: #fbfcfb;
        }

        .benefit-row span {
          color:
            var(--muted);

          font-size: 11px;
        }

        .benefit-row strong {
          color:
            var(--text2);

          font-size: 12px;
          font-weight: 850;

          text-align: right;
        }

        .benefit-row.highlight {
          border-color:
            #d9eadf;

          background:
            var(--primary-soft2);
        }

        .benefit-row.highlight strong {
          color:
            var(--primary);
        }

        /* ================================================
           ERROR
        ================================================ */

        .field-error {
          display: block;

          margin-top: 8px;

          color:
            var(--danger);

          font-size: 12px;
          font-weight: 750;
        }

        /* ================================================
           DIVIDER
        ================================================ */

        .agro-divider {
          height: 1px;

          margin: 30px 0;

          background:
            var(--border);
        }

        /* ================================================
           FORM
        ================================================ */

        .form-grid {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 18px;
        }

        .field-label {
          display: block;

          margin-bottom: 8px;

          color:
            var(--text2);

          font-size: 14px;
          font-weight: 850;
        }

        .amount-wrap,
        .password-wrap {
          position: relative;
        }

        .amount-currency {
          position: absolute;

          left: 16px;
          top: 50%;

          transform:
            translateY(-50%);

          color:
            var(--primary);

          font-size: 21px;
          font-weight: 900;

          z-index: 2;
        }

        .amount-input {
          width: 100%;
          height: 57px;

          padding:
            0 15px 0 43px;

          border:
            1.5px solid
            #dce5df;

          border-radius: 13px;

          outline: 0;

          background: #fff;

          color:
            var(--text);

          font-size: 17px;
          font-weight: 850;

          transition: .2s ease;
        }

        .amount-input:focus,
        .password-input:focus {
          border-color:
            var(--primary);

          box-shadow:
            0 0 0 4px
            rgba(22,128,60,.08);
        }

        .amount-input:disabled {
          background: #f7f9f8;

          color: #9aa49d;

          cursor:
            not-allowed;
        }

        .password-input {
          width: 100%;
          height: 57px;

          padding:
            0 79px 0 15px;

          border:
            1.5px solid
            #dce5df;

          border-radius: 13px;

          outline: 0;

          background: #fff;

          color:
            var(--text);

          font-size: 14px;
          font-weight: 650;

          transition: .2s ease;
        }

        .password-toggle {
          position: absolute;

          right: 8px;
          top: 10px;

          height: 37px;

          padding:
            0 12px;

          border: 0;

          border-radius: 9px;

          background:
            var(--primary-soft);

          color:
            var(--primary);

          font-size: 11px;
          font-weight: 850;

          cursor: pointer;
        }

        .field-help {
          margin-top: 7px;

          color:
            var(--muted);

          font-size: 11px;

          line-height: 1.55;
        }

        /* ================================================
           QUICK AMOUNT
        ================================================ */

        .quick-amount {
          display: flex;

          flex-wrap: wrap;

          gap: 7px;

          margin-top: 9px;
        }

        .quick-amount button {
          padding:
            7px 10px;

          border:
            1px solid
            var(--border);

          border-radius: 8px;

          background: #fff;

          color:
            var(--text2);

          font-size: 11px;
          font-weight: 750;

          cursor: pointer;

          transition: .18s ease;
        }

        .quick-amount button:hover {
          border-color:
            var(--primary);

          background:
            var(--primary-soft);

          color:
            var(--primary);
        }

        /* ================================================
           CALCULATION
        ================================================ */

        .calculation-grid {
          display: grid;

          grid-template-columns:
            repeat(3,1fr);

          gap: 12px;

          margin-top: 22px;
        }

        .calculation-item {
          padding: 15px;

          border:
            1px solid
            var(--border);

          border-radius: 14px;

          background:
            #fafcfb;
        }

        .calculation-item span {
          display: block;

          margin-bottom: 5px;

          color:
            var(--muted);

          font-size: 11px;
        }

        .calculation-item strong {
          display: block;

          color:
            var(--text);

          font-size: 15px;
          font-weight: 850;
        }

        .calculation-item.total {
          border-color:
            #cfe5d6;

          background:
            var(--primary-soft);
        }

        .calculation-item.total strong {
          color:
            var(--primary);

          font-size: 18px;
        }

        /* ================================================
           INFO NOTICE
        ================================================ */

        .investment-notice {
          display: flex;

          align-items: flex-start;

          gap: 11px;

          padding: 14px;

          margin-top: 19px;

          border:
            1px solid
            #dceadf;

          border-radius: 13px;

          background:
            #f5faf6;
        }

        .investment-notice-icon {
          flex: 0 0 30px;

          width: 30px;
          height: 30px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 9px;

          background:
            var(--primary-soft);

          color:
            var(--primary);

          font-size: 15px;
          font-weight: 900;
        }

        .investment-notice p {
          margin: 0;

          color:
            #526259;

          font-size: 11px;

          line-height: 1.65;
        }

        /* ================================================
           TERMS
        ================================================ */

        .terms-box {
          display: flex;

          align-items: flex-start;

          gap: 11px;

          padding: 15px;

          margin-top: 18px;

          border:
            1px solid
            var(--border);

          border-radius: 13px;

          background:
            #f8faf9;
        }

        .terms-box input {
          flex: 0 0 19px;

          width: 19px;
          height: 19px;

          margin-top: 2px;

          accent-color:
            var(--primary);

          cursor: pointer;
        }

        .terms-box label {
          margin: 0;

          color:
            #536159;

          font-size: 12px;

          line-height: 1.65;

          cursor: pointer;
        }

        /* ================================================
           BUTTON
        ================================================ */

        .investment-button {
          width: 100%;

          min-height: 59px;

          display: flex;

          align-items: center;
          justify-content: center;

          gap: 10px;

          margin-top: 19px;

          padding:
            13px 20px;

          border: 0;

          border-radius: 14px;

          background:
            linear-gradient(
              135deg,
              var(--primary),
              var(--primary-dark)
            );

          color: #fff;

          font-size: 15px;
          font-weight: 900;

          cursor: pointer;

          box-shadow:
            0 12px 27px
            rgba(22,128,60,.20);

          transition: .2s ease;
        }

        .investment-button:hover:not(:disabled) {
          transform:
            translateY(-1px);

          box-shadow:
            0 16px 32px
            rgba(22,128,60,.25);
        }

        .investment-button:disabled {
          opacity: .48;

          cursor:
            not-allowed;

          box-shadow: none;
        }

        .button-spinner {
          width: 20px;
          height: 20px;

          border:
            2px solid
            rgba(255,255,255,.35);

          border-top-color:
            #fff;

          border-radius: 50%;

          animation:
            agroSpin
            .7s linear infinite;
        }

        @keyframes agroSpin {
          to {
            transform:
              rotate(360deg);
          }
        }

        /* ================================================
           SUMMARY
        ================================================ */

        .summary-panel {
          position: sticky;

          top: 20px;
        }

        .summary-header {
          position: relative;

          overflow: hidden;

          padding: 23px;

          background:
            linear-gradient(
              135deg,
              #082f1b,
              #0e6730
            );

          color: #fff;
        }

        .summary-header::after {
          content: "";

          position: absolute;

          width: 130px;
          height: 130px;

          right: -60px;
          top: -65px;

          border-radius: 50%;

          background:
            rgba(255,255,255,.07);
        }

        .summary-header small {
          display: block;

          margin-bottom: 4px;

          color:
            rgba(255,255,255,.6);

          font-size: 11px;
        }

        .summary-header h2 {
          margin: 0;

          color: #fff;

          font-size: 21px;
          font-weight: 900;
        }

        .summary-body {
          padding: 21px;
        }

        .summary-package {
          padding: 15px;

          margin-bottom: 14px;

          border:
            1px solid
            #dcebe1;

          border-radius: 14px;

          background:
            #f4faf6;
        }

        .summary-package small {
          display: block;

          margin-bottom: 4px;

          color:
            var(--primary);

          font-size: 10px;
          font-weight: 850;

          text-transform:
            uppercase;

          letter-spacing: .6px;
        }

        .summary-package strong {
          display: block;

          color:
            var(--text);

          font-size: 15px;
          font-weight: 900;

          line-height: 1.4;
        }

        .summary-package p {
          margin:
            6px 0 0;

          color:
            var(--muted);

          font-size: 11px;

          line-height: 1.55;
        }

        .summary-row {
          display: flex;

          align-items: flex-start;
          justify-content:
            space-between;

          gap: 15px;

          padding:
            12px 1px;

          border-bottom:
            1px solid
            #edf1ee;
        }

        .summary-row span {
          color:
            var(--muted);

          font-size: 12px;
        }

        .summary-row strong {
          max-width: 58%;

          color:
            var(--text2);

          font-size: 12px;
          font-weight: 850;

          text-align: right;

          overflow-wrap:
            anywhere;
        }

        .summary-row strong.green {
          color:
            var(--primary);
        }

        .summary-total {
          margin-top: 17px;

          padding: 18px;

          border-radius: 15px;

          background:
            linear-gradient(
              135deg,
              #082f1b,
              #0d5f2a
            );
        }

        .summary-total span {
          display: block;

          margin-bottom: 5px;

          color:
            rgba(255,255,255,.62);

          font-size: 12px;
        }

        .summary-total strong {
          display: block;

          color: #fff;

          font-size: 27px;
          font-weight: 950;

          line-height: 1.2;
        }

        .summary-note {
          padding: 13px;

          margin-top: 14px;

          border:
            1px solid
            #f2e4bd;

          border-radius: 12px;

          background:
            var(--warning-soft);

          color:
            #7c590a;

          font-size: 11px;

          line-height: 1.65;
        }

        /* ================================================
           STATUS / LOADING
        ================================================ */

        .package-status {
          padding:
            40px 20px;

          border:
            1px dashed
            #d4ded7;

          border-radius: 16px;

          background:
            #fafcfb;

          text-align: center;

          color:
            var(--muted);

          font-size: 13px;
        }

        .package-status strong {
          display: block;

          margin-bottom: 5px;

          color:
            var(--text);

          font-size: 16px;
        }

        .package-loading-grid {
          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 17px;
        }

        .package-skeleton {
          height: 390px;

          border-radius: 20px;

          background:
            linear-gradient(
              90deg,
              #eef2ef 25%,
              #fafcfb 37%,
              #eef2ef 63%
            );

          background-size:
            400% 100%;

          animation:
            packageShimmer
            1.35s ease infinite;
        }

        @keyframes packageShimmer {
          0% {
            background-position:
              100% 0;
          }

          100% {
            background-position:
              -100% 0;
          }
        }

        /* ================================================
           TABLET
        ================================================ */

        @media(max-width:1100px) {

          .agro-main-grid {
            grid-template-columns:
              1fr;
          }

          .summary-panel {
            position: static;
          }

        }

        /* ================================================
           MOBILE
        ================================================ */

        @media(max-width:760px) {

          .agro-page {
            padding: 2px;
          }

          .agro-hero {
            flex-direction:
              column;

            align-items:
              flex-start;

            min-height: auto;

            padding:
              27px 21px;

            border-radius:
              20px;
          }

          .agro-hero h1 {
            font-size: 28px;
          }

          .agro-hero p {
            font-size: 13px;
          }

          .hero-security {
            width: 100%;

            flex-basis: auto;
          }

          .agro-panel-body {
            padding: 18px;
          }

          .package-grid,
          .package-loading-grid {
            grid-template-columns:
              1fr;
          }

          .package-card {
            min-height: auto;
          }

          .form-grid {
            grid-template-columns:
              1fr;
          }

          .calculation-grid {
            grid-template-columns:
              1fr;
          }

          .section-head h2 {
            font-size: 19px;
          }

          .summary-total strong {
            font-size: 24px;
          }

        }

      `}</style>

      <main className="agro-page" style={{background: "white",borderRadius: "20px"}}>

        <div className="agro-shell">


          {/* ============================================
              MAIN
          ============================================ */}

          <div>

            {/* ==========================================
                LEFT
            ========================================== */}

            <section className="agro-panel">

              <div className="agro-panel-body">

                {/* ======================================
                    STEP 1
                ====================================== */}

                <div className="section-head">

                  <div className="section-step">
                    01
                  </div>

                  <div>

                    <h2>
                      Select Agro Investment Package
                    </h2>

                    <p>
                      Compare the available agro packages,
                      minimum booking amount, investment
                      target and package benefits.
                    </p>

                  </div>

                </div>

                {/* ======================================
                    PACKAGES
                ====================================== */}

                {fetchLoading ? (

                  <div className="package-loading-grid">

                    <div className="package-skeleton"></div>

                    <div className="package-skeleton"></div>

                  </div>

                ) : pageError ? (

                  <div className="package-status">

                    <strong>
                      Unable to Load Agro Packages
                    </strong>

                    {pageError}

                  </div>

                ) : projects.length === 0 ? (

                  <div className="package-status">

                    <strong>
                      No Agro Investment Package Available
                    </strong>

                    Please check again later.

                  </div>

                ) : (

                  <div className="package-grid">

                    {projects.map(
                      (project, index) => {

                        const selected =
                          String(
                            selectedProjectId
                          ) ===
                          String(
                            project.id
                          );

                        const benefits =
                          getPackageBenefits(
                            project,
                            index
                          );

                        return (

                          <label
                            key={
                              project.id ||
                              index
                            }
                            className={
                              `package-card ${
                                selected
                                  ? "selected"
                                  : ""
                              } ${
                                index === 1
                                  ? "featured"
                                  : ""
                              }`
                            }
                          >

                            <input
                              type="radio"
                              name="selectedProject"
                              className="package-radio"
                              value={project.id}
                              checked={selected}
                              onChange={() =>
                                selectProject(
                                  project
                                )
                              }
                            />

                            <span className="package-check">
                              <CheckIcon />
                            </span>

                            {/* TOP */}

                            <div className="package-top">

                              <div className="package-icon">
                                <LeafIcon />
                              </div>

                              <div>

                                <div className="package-category">
                                  {getPackageBadge(
                                    index
                                  )}
                                </div>

                                <div className="package-name">
                                  {getPackageName(
                                    project,
                                    index
                                  )}
                                </div>

                              </div>

                            </div>

                            <div className="package-subtitle">

                              {getPackageSubtitle(
                                index
                              )}

                            </div>

                            {/* TARGET */}

                            <div className="target-box">

                              <span>
                                Investment Target
                              </span>

                              <strong>
                                {money(
                                  project.price
                                )}
                              </strong>

                            </div>

                            {/* BENEFITS */}

                            <div className="benefit-list">

                              {benefits.map(
                                (
                                  benefit,
                                  benefitIndex
                                ) => (

                                  <div
                                    className={
                                      `benefit-row ${
                                        benefitIndex >=
                                        2
                                          ? "highlight"
                                          : ""
                                      }`
                                    }
                                    key={
                                      benefit.label
                                    }
                                  >

                                    <span>
                                      {
                                        benefit.label
                                      }
                                    </span>

                                    <strong>
                                      {
                                        benefit.value
                                      }
                                    </strong>

                                  </div>

                                )
                              )}

                            </div>

                          </label>

                        );
                      }
                    )}

                  </div>

                )}

                {selectedError && (

                  <span className="field-error">
                    {selectedError}
                  </span>

                )}

                <div className="agro-divider"></div>

                {/* ======================================
                    STEP 2
                ====================================== */}

                <div className="section-head">

                  <div className="section-step">
                    02
                  </div>

                  <div>

                    <h2>
                      Investment Details
                    </h2>

                    <p>
                      Enter your booking amount and
                      authorize the investment using
                      your account password.
                    </p>

                  </div>

                </div>

                {/* ======================================
                    FORM
                ====================================== */}

                <form
                  onSubmit={
                    submitAgroInvestment
                  }
                  noValidate
                >

                  <div className="form-grid">

                    {/* ================================
                        BOOKING AMOUNT
                    ================================ */}

                    <div>

                      <label className="field-label">
                        Booking Amount
                      </label>

                      <div className="amount-wrap">

                        <span className="amount-currency">
                          ৳
                        </span>

                        <input
                          type="text"
                          inputMode="decimal"
                          name="amount"
                          className="amount-input"
                          placeholder={
                            selectedProject
                              ? `Minimum ${money(
                                  minimumBooking
                                )}`
                              : "Select a package first"
                          }
                          value={
                            form.amount
                          }
                          onChange={
                            handleInput
                          }
                          disabled={
                            !selectedProject
                          }
                        />

                      </div>

                      {/* QUICK AMOUNT */}

                      {selectedProject && (

                        <div className="quick-amount">

                          <button
                            type="button"
                            onClick={() =>
                              setQuickAmount(
                                minimumBooking
                              )
                            }
                          >
                            Minimum{" "}
                            {money(
                              minimumBooking
                            )}
                          </button>

                          {investmentTarget >
                            minimumBooking && (

                            <button
                              type="button"
                              onClick={() =>
                                setQuickAmount(
                                  investmentTarget
                                )
                              }
                            >
                              Full Target{" "}
                              {money(
                                investmentTarget
                              )}
                            </button>

                          )}

                        </div>

                      )}

                      <div className="field-help">

                        {selectedProject
                          ? `Booking starts from ${money(
                              minimumBooking
                            )}. Investment target is ${money(
                              investmentTarget
                            )}.`
                          : "Please select an agro investment package first."}

                      </div>

                      {amountError && (

                        <span className="field-error">
                          {amountError}
                        </span>

                      )}

                    </div>

                    {/* ================================
                        PASSWORD
                    ================================ */}

                    <div>

                      <label className="field-label">
                        Account Password
                      </label>

                      <div className="password-wrap">

                        <input
                          type={
                            showPassword
                              ? "text"
                              : "password"
                          }
                          name="password"
                          className="password-input"
                          placeholder="Enter your account password"
                          value={
                            form.password
                          }
                          onChange={
                            handleInput
                          }
                          autoComplete="current-password"
                        />

                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() =>
                            setShowPassword(
                              (previous) =>
                                !previous
                            )
                          }
                        >
                          {showPassword
                            ? "Hide"
                            : "Show"}
                        </button>

                      </div>

                      <div className="field-help">
                        Your password is required
                        to authorize this investment.
                      </div>

                      {passwordError && (

                        <span className="field-error">
                          {passwordError}
                        </span>

                      )}

                    </div>

                  </div>

                  {/* ====================================
                      CALCULATION
                  ==================================== */}

                  <div className="calculation-grid">

                    <div className="calculation-item">

                      <span>
                        Minimum Booking
                      </span>

                      <strong>

                        {selectedProject
                          ? money(
                              minimumBooking
                            )
                          : "—"}

                      </strong>

                    </div>

                    <div className="calculation-item">

                      <span>
                        Investment Target
                      </span>

                      <strong>

                        {selectedProject
                          ? money(
                              investmentTarget
                            )
                          : "—"}

                      </strong>

                    </div>

                    <div className="calculation-item total">

                      <span>
                        Your Booking Amount
                      </span>

                      <strong>

                        {selectedProject
                          ? money(
                              totalPayable
                            )
                          : "৳0"}

                      </strong>

                    </div>

                  </div>

                  {/* ====================================
                      NOTICE
                  ==================================== */}

                  <div className="investment-notice">

                    <div className="investment-notice-icon">
                      i
                    </div>

                    <p>
                      Your booking amount will be
                      recorded under the selected agro
                      investment package. Package return
                      eligibility starts according to the
                      applicable package terms after the
                      required investment target is
                      completed.
                    </p>

                  </div>






















              <div className="summary-body">

                {selectedProject ? (

                  <>

                    <div className="summary-package">

                      <small>
                        {getPackageBadge(
                          selectedProjectIndex
                        )}
                      </small>

                      <strong>
                        {getPackageName(
                          selectedProject,
                          selectedProjectIndex
                        )}
                      </strong>

                      <p>
                        {getPackageSubtitle(
                          selectedProjectIndex
                        )}
                      </p>

                    </div>

                    <div className="summary-row">

                      <span>
                        Minimum Booking
                      </span>

                      <strong>
                        {money(
                          minimumBooking
                        )}
                      </strong>

                    </div>

                    <div className="summary-row">

                      <span>
                        Investment Target
                      </span>

                      <strong>
                        {money(
                          investmentTarget
                        )}
                      </strong>

                    </div>

                    {selectedProjectIndex ===
                    0 ? (

                      <>

                        <div className="summary-row">

                          <span>
                            Monthly Return
                          </span>

                          <strong className="green">
                            3% – 4%
                          </strong>

                        </div>

                        <div className="summary-row">

                          <span>
                            Sponsor Commission
                          </span>

                          <strong className="green">
                            5%
                          </strong>

                        </div>

                      </>

                    ) : (

                      <>

                        <div className="summary-row">

                          <span>
                            Monthly Return
                          </span>

                          <strong className="green">
                            10%
                          </strong>

                        </div>

                        <div className="summary-row">

                          <span>
                            Maximum Return
                          </span>

                          <strong className="green">
                            Up to 2X
                          </strong>

                        </div>

                        <div className="summary-row">

                          <span>
                            Spot Commission
                          </span>

                          <strong className="green">
                            5%
                          </strong>

                        </div>

                      </>

                    )}

                    <div className="summary-total">

                      <span>
                        Current Booking Amount
                      </span>

                      <strong>
                        {money(
                          totalPayable
                        )}
                      </strong>

                    </div>

                    <div className="summary-note">

                      Returns and commissions are
                      subject to the selected package
                      terms, eligibility requirements
                      and applicable investment
                      conditions.

                    </div>

                  </>

                ) : (

                  <div className="package-status">

                    <strong>
                      Select a Package
                    </strong>

                    Choose an agro investment
                    package to view the complete
                    booking summary.

                  </div>

                )}

              </div>





















                  {/* ====================================
                      TERMS
                  ==================================== */}

                  <div className="terms-box">

                    <input
                      type="checkbox"
                      id="agro-investment-terms"
                      checked={
                        termsAccepted
                      }
                      onChange={(event) => {

                        setTermsAccepted(
                          event.target.checked
                        );

                        setForm(
                          (previous) => ({
                            ...previous,

                            error_list: {
                              ...previous
                                .error_list,

                              terms: "",
                            },
                          })
                        );

                      }}
                    />

                    <label htmlFor="agro-investment-terms">

                      I have reviewed the selected
                      agro investment package,
                      investment target, minimum
                      booking amount and applicable
                      package information. I agree
                      to the applicable terms and
                      conditions.

                    </label>

                  </div>

                  {termsError && (

                    <span className="field-error">
                      {termsError}
                    </span>

                  )}

                  {/* ====================================
                      SUBMIT
                  ==================================== */}

                  <button
                    type="submit"
                    className="investment-button"
                    disabled={
                      !canSubmit
                    }
                  >

                    {submitting ? (

                      <>

                        <span className="button-spinner"></span>

                        Processing Investment...

                      </>

                    ) : selectedProject ? (

                      <>

                        Confirm Agro Investment •{" "}

                        {money(
                          totalPayable
                        )}

                      </>

                    ) : (

                      "Select an Agro Package to Continue"

                    )}

                  </button>

                </form>

              </div>

            </section>

            {/* ==========================================
                RIGHT SUMMARY
            ========================================== */}


          </div>

        </div>

      </main>

    </Fragment>
  );
};

export default Checkout;